import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, Ban, BookOpen, Camera, Check, Code2, Copy, Download, Eye, FlaskConical,
  History, List, LogOut, Maximize2, MessageSquare, Monitor, Plus, RefreshCw, Search, Settings, Shield,
  ShieldAlert, ShieldCheck, Snowflake, Square, Trash2, Users, Webhook, X, UserCheck, Flame, Waves, UserCog, Globe,
  TrendingUp, Circle,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import PanelShell from "@/components/PanelShell";
import SecretKey, { maskKey } from "@/components/SecretKey";
import { API } from "@/App";
import { tokenStore } from "@/lib/token";

// Tableau de bord de VantaGuard (anti-cheat FiveM) : tout se règle ici, la
// ressource du serveur de jeu reçoit les changements en quelques secondes.

const auth = () => ({ headers: { Authorization: `Bearer ${tokenStore.get()}` } });
const ac = {
  get: (path, params) => axios.get(`${API}/ac${path}`, { ...auth(), params }).then((r) => r.data),
  post: (path, body) => axios.post(`${API}/ac${path}`, body || {}, auth()).then((r) => r.data),
  put: (path, body) => axios.put(`${API}/ac${path}`, body, auth()).then((r) => r.data),
  patch: (path, body) => axios.patch(`${API}/ac${path}`, body, auth()).then((r) => r.data),
  del: (path) => axios.delete(`${API}/ac${path}`, auth()).then((r) => r.data),
};
const errorText = (err, fallback) => err?.response?.data?.detail || fallback;

const TABS = [
  { id: "overview", label: "Vue d'ensemble", icon: Activity },
  { id: "players", label: "Joueurs", icon: Users },
  { id: "detections", label: "Détections", icon: ShieldAlert },
  { id: "bans", label: "Bans", icon: Ban },
  { id: "captures", label: "Captures", icon: Camera },
  { id: "modules", label: "Modules", icon: Shield },
  { id: "lists", label: "Listes", icon: List },
  { id: "scripts", label: "Scripts", icon: Code2 },
  { id: "webhooks", label: "Webhooks", icon: Webhook },
  { id: "logs", label: "Journal", icon: History },
  { id: "settings", label: "Paramètres", icon: Settings },
  { id: "team", label: "Équipe", icon: UserCog },
  { id: "install", label: "Installation", icon: Download },
];
// Onglets réservés aux administrateurs (et au propriétaire).
const TAB_HINTS = {
  overview: "État du serveur et activité", players: "Joueurs connectés", detections: "Ce que l'anti-cheat a vu",
  bans: "Bans et vague", captures: "Captures d'écran", modules: "Détections et sanctions", lists: "Listes blanches et noires",
  scripts: "Ressources autorisées", webhooks: "Alertes Discord", logs: "Historique des actions", settings: "Réglages généraux",
  team: "Accès du staff", install: "Installer la ressource",
};
const ADMIN_TABS = ["modules", "lists", "scripts", "webhooks", "settings", "team", "install"];
const RANK = { viewer: 1, moderator: 2, admin: 3, owner: 4 };
const CONFIG_TABS = ["modules", "lists", "scripts", "webhooks", "settings"];

const ACTIONS = {
  log: { label: "Journaliser", done: "Journalisé", icon: Eye, cls: "text-sky-300 bg-sky-500/10 border-sky-500/30" },
  kick: { label: "Expulser", done: "Expulsé", icon: LogOut, cls: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
  ban: { label: "Bannir", done: "Banni", icon: Ban, cls: "text-red-300 bg-red-500/10 border-red-500/30" },
  wave: { label: "Vague", done: "Ban en vague", icon: Waves, cls: "text-fuchsia-300 bg-fuchsia-500/10 border-fuchsia-500/30" },
};
const DURATIONS = [
  { hours: 1, label: "1 heure" }, { hours: 24, label: "1 jour" }, { hours: 72, label: "3 jours" },
  { hours: 168, label: "7 jours" }, { hours: 720, label: "30 jours" }, { hours: 0, label: "Définitif" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};
const since = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
  return `${Math.floor(s / 86400)} j`;
};
const durationLabel = (hours) => (DURATIONS.find((d) => d.hours === Number(hours)) || { label: `${hours} h` }).label;
const idsOf = (ids) => {
  const out = {};
  (ids || []).forEach((i) => { const [k, ...v] = i.split(":"); if (!out[k]) out[k] = v.join(":"); });
  return out;
};

// ── Petits composants ──

function Panel({ title, icon: Icon, actions, children, className = "" }) {
  return (
    <section className={`bg-[#12121a] border border-white/5 rounded-lg ${className}`}>
      {title && (
        <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10">
          <h2 className="font-heading font-semibold text-sm flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-red-400" />}{title}</h2>
          {actions}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

function Btn({ children, onClick, variant = "ghost", disabled, className = "", title, type = "button", ...rest }) {
  const styles = {
    primary: "bg-red-600 hover:bg-red-500 text-white border-red-500",
    ghost: "bg-white/5 hover:bg-white/10 text-white border-white/10",
    danger: "bg-red-500/10 hover:bg-red-500/20 text-red-200 border-red-500/30",
    success: "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 border-emerald-500/30",
  };
  return (
    <button type={type} title={title} onClick={onClick} disabled={disabled} {...rest}
      className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${styles[variant]} ${className}`}>
      {children}
    </button>
  );
}

function Toggle({ checked, onChange, label, disabled, testId }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} data-testid={testId}
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-5 shrink-0 rounded-full transition-colors ${checked ? "bg-red-600" : "bg-white/15"} disabled:opacity-40`}>
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
    </button>
  );
}

function ActionBadge({ action, test }) {
  const a = ACTIONS[action] || ACTIONS.log;
  const Icon = a.icon;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] border ${a.cls}`}><Icon className="w-3 h-3" />{a.done}</span>
      {test && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] border text-violet-200 bg-violet-500/10 border-violet-500/30"><FlaskConical className="w-3 h-3" />Mode test</span>}
    </span>
  );
}

function Modal({ title, onClose, children, wide }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start sm:items-center justify-center p-3 overflow-y-auto" onMouseDown={onClose}>
      <div role="dialog" aria-label={title} onMouseDown={(e) => e.stopPropagation()}
        className={`w-full ${wide ? "max-w-5xl" : "max-w-lg"} bg-[#12121a] border border-white/5 rounded-lg my-6`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <h3 className="font-heading font-semibold">{title}</h3>
          <button onClick={onClose} className="text-text-muted hover:text-white p-1" aria-label="Fermer"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

const shotCache = new Map();
function AuthImage({ botId, shotId, className, alt, onClick }) {
  const [src, setSrc] = useState(shotCache.get(shotId) || null);
  useEffect(() => {
    if (!shotId || shotCache.has(shotId)) { setSrc(shotCache.get(shotId) || null); return undefined; }
    let alive = true;
    axios.get(`${API}/ac/${botId}/screenshots/${shotId}`, { ...auth(), responseType: "blob" })
      .then((r) => { const url = URL.createObjectURL(r.data); shotCache.set(shotId, url); if (alive) setSrc(url); })
      .catch(() => {});
    return () => { alive = false; };
  }, [botId, shotId]);
  if (!src) return <div className={`${className} bg-black/40 animate-pulse`} />;
  return <img src={src} alt={alt || "Capture"} className={`${className} object-cover ${onClick ? "cursor-zoom-in" : ""}`} onClick={onClick} />;
}

// `secret` : texte masqué à l'affichage (la copie garde le vrai texte).
function CodeLines({ lines, secret }) {
  const text = lines.join("\n");
  const shown = secret ? text.split(secret).join(maskKey(secret)) : text;
  return (
    <div className="relative bg-black/50 border border-white/10">
      <pre className="p-3 pr-12 text-xs font-mono text-emerald-200 overflow-x-auto whitespace-pre">{shown}</pre>
      <button onClick={() => { navigator.clipboard.writeText(text); toast.success("Copié"); }}
        className="absolute top-2 right-2 p-1.5 text-text-muted hover:text-white" title="Copier"><Copy className="w-3.5 h-3.5" /></button>
    </div>
  );
}

function ChipEditor({ values, onChange, placeholder, numeric, testId }) {
  const [input, setInput] = useState("");
  const add = () => {
    const parts = input.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean)
      .map((s) => (numeric ? Number(s) : s)).filter((v) => (numeric ? Number.isInteger(v) && v >= 0 && v <= 200 : true));
    const next = [...values];
    parts.forEach((p) => { if (!next.includes(p)) next.push(p); });
    onChange(next);
    setInput("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
        {values.length === 0 && <span className="text-xs text-text-muted">Liste vide.</span>}
        {values.map((v) => (
          <span key={String(v)} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 text-xs bg-white/5 border border-white/10 font-mono">
            {String(v)}
            <button onClick={() => onChange(values.filter((x) => x !== v))} className="text-text-muted hover:text-red-300 p-0.5" aria-label={`Retirer ${v}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} data-testid={testId}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder} className="flex-1 min-w-0 bg-black/40 border border-white/10 px-2 py-1.5 text-sm font-mono" />
        <Btn onClick={add} disabled={!input.trim()}><Plus className="w-4 h-4" />Ajouter</Btn>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-black/40 border border-white/10 px-2 py-1.5 text-sm";

// ── Graphique : détections par jour (une seule série, survol = détail) ──

function DailyChart({ days }) {
  const [hover, setHover] = useState(null);
  const [table, setTable] = useState(false);
  const max = Math.max(1, ...days.map((d) => d.detections));
  const top = Math.max(...days.map((d) => d.detections));
  const step = max <= 4 ? 1 : Math.ceil(max / 4);
  const ticks = [0];
  while (ticks[ticks.length - 1] < max) ticks.push(ticks[ticks.length - 1] + step);
  const scaleMax = ticks[ticks.length - 1];
  const peakIndex = days.findIndex((d) => d.detections === top && top > 0);
  const label = (date) => new Date(`${date}T12:00:00Z`).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-text-muted">Détections par jour, 14 derniers jours</p>
        <button onClick={() => setTable(!table)} className="text-xs text-text-muted hover:text-white underline decoration-dotted">{table ? "Voir le graphique" : "Voir en tableau"}</button>
      </div>
      {table ? (
        <table className="w-full text-xs">
          <thead><tr className="text-text-muted text-left"><th className="py-1">Jour</th><th>Détections</th><th>Expulsions</th><th>Bans</th></tr></thead>
          <tbody>{days.map((d) => <tr key={d.date} className="border-t border-white/10"><td className="py-1">{label(d.date)}</td><td>{d.detections}</td><td>{d.kicks}</td><td>{d.bans}</td></tr>)}</tbody>
        </table>
      ) : (
        <div className="relative h-44 pl-8 mt-6">
          {ticks.map((t) => (
            <div key={t} className="absolute left-8 right-0 border-t border-white/[0.06]" style={{ bottom: `${(t / scaleMax) * 100}%` }}>
              <span className="absolute -left-8 -top-2 w-6 text-right text-[10px] text-text-muted">{t}</span>
            </div>
          ))}
          <div className="absolute inset-0 left-8 flex items-end gap-0.5">
            {days.map((d, i) => (
              <div key={d.date} className="relative flex-1 h-full flex items-end justify-center"
                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                {i === peakIndex && (
                  <span className="absolute text-[10px] text-text-secondary" style={{ bottom: `calc(${(d.detections / scaleMax) * 100}% + 4px)` }}>{d.detections}</span>
                )}
                <div className="w-full max-w-[24px] rounded-t-[4px] transition-opacity"
                  style={{ height: `${(d.detections / scaleMax) * 100}%`, background: "#3987e5", opacity: hover === null || hover === i ? 1 : 0.55, minHeight: d.detections ? 2 : 0 }} />
                {hover === i && (
                  <div className="absolute z-10 bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap bg-black/90 border border-white/10 px-2.5 py-1.5 text-[11px] pointer-events-none">
                    <div className="font-semibold text-white mb-0.5">{label(d.date)}</div>
                    <div className="text-text-secondary">{d.detections} détection(s)</div>
                    <div className="text-text-secondary">{d.kicks} expulsion(s) · {d.bans} ban(s)</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {!table && (
        <div className="flex pl-8 mt-1 text-[10px] text-text-muted">
          {days.map((d, i) => <span key={d.date} className="flex-1 text-center">{i % 2 === 0 ? label(d.date) : ""}</span>)}
        </div>
      )}
    </div>
  );
}

function TrustBadge({ score, label }) {
  if (score === undefined || score === null) return <span className="text-text-muted">—</span>;
  const tone = score >= 70 ? "text-emerald-300" : score >= 40 ? "text-amber-300" : "text-red-300";
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs ${tone}`} title="Score de confiance sur 100 : baisse avec les détections des 30 derniers jours et les anciens bans">
      <Circle className="w-2 h-2 fill-current" /><span className="font-mono">{score}</span><span className="text-text-muted">{label}</span>
    </span>
  );
}

function StatTile({ label, value, hint, icon: Icon, tone = "text-white" }) {
  return (
    <div className="bg-[#12121a] border border-white/5 rounded-lg p-4">
      <div className="flex items-center gap-2 text-xs text-text-muted mb-1">{Icon && <Icon className="w-3.5 h-3.5" />}{label}</div>
      <div className={`text-2xl font-semibold ${tone}`}>{value}</div>
      {hint && <div className="text-[11px] text-text-muted mt-0.5">{hint}</div>}
    </div>
  );
}

// ── Écran en direct ──

function LiveViewer({ botId, session, player, onClose, onScreenshot }) {
  // Les images sont décodées hors du fil principal (createImageBitmap) puis
  // peintes sur un canvas à la cadence de l'écran : seule la plus récente
  // compte, une image en retard est sautée plutôt qu'affichée en retard.
  const canvasRef = useRef(null);
  const frames = useRef(0);
  const decoding = useRef(false);
  const pending = useRef(null);
  const [status, setStatus] = useState({ text: "Connexion au relais…", pusher: false, ended: false });
  const [info, setInfo] = useState(null);
  const [fps, setFps] = useState(0);
  const [hasFrame, setHasFrame] = useState(false);
  const [failure, setFailure] = useState(null);
  // Enregistrement du direct en vidéo (WebM), dans le navigateur.
  const recorder = useRef(null);
  const chunks = useRef([]);
  const [recording, setRecording] = useState(0);

  const paint = useCallback(async (buffer) => {
    if (decoding.current) { pending.current = buffer; return; }
    decoding.current = true;
    let next = buffer;
    while (next) {
      pending.current = null;
      try {
        const blob = new Blob([next], { type: "image/jpeg" });
        const bitmap = typeof createImageBitmap === "function" ? await createImageBitmap(blob) : await new Promise((resolve, reject) => {
          const img = new Image();
          const url = URL.createObjectURL(blob);
          img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
          img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("image")); };
          img.src = url;
        });
        const canvas = canvasRef.current;
        if (canvas) {
          const w = bitmap.width;
          const h = bitmap.height;
          if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
          await new Promise((resolve) => requestAnimationFrame(resolve));
          canvas.getContext("2d", { alpha: false }).drawImage(bitmap, 0, 0);
          frames.current += 1;
          setHasFrame(true);
        }
        if (bitmap.close) bitmap.close();
      } catch { /* image abîmée : on passe à la suivante */ }
      next = pending.current;
    }
    decoding.current = false;
  }, []);

  const stopRecording = useCallback(() => {
    if (recorder.current && recorder.current.state !== "inactive") recorder.current.stop();
  }, []);

  const startRecording = () => {
    const canvas = canvasRef.current;
    if (!canvas?.width || !canvas.captureStream || typeof MediaRecorder === "undefined") {
      toast.error("Votre navigateur ne permet pas d'enregistrer le direct");
      return;
    }
    const type = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"].find((t) => MediaRecorder.isTypeSupported(t));
    const rec = new MediaRecorder(canvas.captureStream(30), type ? { mimeType: type } : undefined);
    chunks.current = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunks.current, { type: "video/webm" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `vantaguard-${(player?.name || "joueur").replace(/[^\w-]+/g, "_")}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.webm`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      recorder.current = null;
      setRecording(0);
      toast.success("Enregistrement téléchargé");
    };
    rec.start(1000);
    recorder.current = rec;
    setRecording(1);
  };

  useEffect(() => {
    if (!recording) return undefined;
    const t = setInterval(() => setRecording((r) => {
      if (r >= 300) { stopRecording(); return r; }
      return r + 1;
    }), 1000);
    return () => clearInterval(t);
  }, [recording > 0, stopRecording]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => stopRecording(), [stopRecording]);

  useEffect(() => {
    const ws = new WebSocket(session.watch_url);
    ws.binaryType = "arraybuffer";
    ws.onopen = () => setStatus((s) => ({ ...s, text: "En attente du jeu du joueur…" }));
    ws.onmessage = (event) => {
      if (typeof event.data === "string") {
        let msg;
        try { msg = JSON.parse(event.data); } catch { return; }
        if (msg.type === "status") setStatus((s) => ({ ...s, pusher: msg.pusher, text: msg.pusher ? "En direct" : (s.ended ? s.text : "En attente du jeu du joueur…") }));
        if (msg.type === "info") setInfo(msg);
        if (msg.type === "error") setFailure(msg.message);
        if (msg.type === "ended") setStatus({ text: msg.reason || "Direct terminé", pusher: false, ended: true });
        return;
      }
      paint(event.data);
    };
    ws.onclose = () => setStatus((s) => (s.ended ? s : { ...s, pusher: false, text: "Connexion au relais fermée" }));
    const timer = setInterval(() => { setFps(frames.current); frames.current = 0; }, 1000);
    return () => {
      clearInterval(timer);
      ws.close();
    };
  }, [session.watch_url, paint]);

  // Le jeu du joueur a refusé ou n'a pas répondu : on l'affiche.
  useEffect(() => {
    if (!session.command_id) return undefined;
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const cmd = await ac.get(`/${botId}/commands/${session.command_id}`);
        if (cmd.status === "failed" || cmd.status === "expired") {
          setFailure(cmd.result?.message || "Le jeu du joueur n'a pas lancé le direct");
          clearInterval(timer);
        } else if (cmd.status === "done" || tries > 15) clearInterval(timer);
      } catch { clearInterval(timer); }
    }, 1500);
    return () => clearInterval(timer);
  }, [botId, session.command_id]);

  const stop = async () => {
    try { await ac.del(`/${botId}/live/${session.session_id}`); } catch { /* déjà terminé */ }
    onClose();
  };

  return (
    <Modal title={`Écran en direct : ${player?.name || "joueur"} (ID ${player?.src ?? "?"})`} onClose={stop} wide>
      <div className="relative bg-black aspect-video border border-white/10 overflow-hidden" data-testid="live-screen">
        <canvas ref={canvasRef} role="img" aria-label="Écran du joueur" className={`w-full h-full object-contain bg-black ${hasFrame ? "" : "hidden"}`} />
        {!hasFrame && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-text-muted text-sm p-6 text-center">
            {failure ? <AlertTriangle className="w-8 h-8 text-amber-400" /> : <RefreshCw className="w-6 h-6 animate-spin" />}
            <span>{failure || status.text}</span>
          </div>
        )}
        <div className="absolute top-2 left-2 flex items-center gap-2 text-[11px]">
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 bg-black/70 border ${status.pusher ? "border-red-500/50 text-red-200" : "border-white/10 text-text-muted"}`}>
            <span className={`w-2 h-2 rounded-full ${status.pusher ? "bg-red-500 animate-pulse" : "bg-white/30"}`} />
            {status.pusher ? "EN DIRECT" : "HORS LIGNE"}
          </span>
          {status.pusher && <span className="px-2 py-0.5 bg-black/70 border border-white/10 text-text-secondary">{fps} images/s</span>}
          {info?.width ? <span className="px-2 py-0.5 bg-black/70 border border-white/10 text-text-secondary">{info.width}×{info.height} · {info.mode}</span> : null}
        </div>
      </div>
      {hasFrame && failure && <p className="text-xs text-amber-300 mt-2">{failure}</p>}
      {status.ended && <p className="text-xs text-text-muted mt-2">{status.text}</p>}
      <div className="flex flex-wrap gap-2 mt-3">
        <Btn onClick={() => canvasRef.current?.requestFullscreen?.()} disabled={!hasFrame}><Maximize2 className="w-4 h-4" />Plein écran</Btn>
        <Btn onClick={onScreenshot}><Camera className="w-4 h-4" />Capture</Btn>
        {recording ? (
          <Btn variant="danger" onClick={stopRecording} data-testid="live-record-stop"><Circle className="w-3 h-3 fill-red-500 text-red-500 animate-pulse" />Arrêter l'enregistrement ({Math.floor(recording / 60)}:{String(recording % 60).padStart(2, "0")})</Btn>
        ) : (
          <Btn onClick={startRecording} disabled={!hasFrame} data-testid="live-record"><Circle className="w-3 h-3 fill-red-500 text-red-500" />Enregistrer</Btn>
        )}
        <Btn variant="danger" onClick={stop}><Square className="w-4 h-4" />Arrêter le direct</Btn>
      </div>
      <p className="text-[11px] text-text-muted mt-3">Seule l'image du jeu est transmise, jamais le reste de l'écran du PC. Le direct s'arrête tout seul quand plus personne ne regarde. L'enregistrement (5 minutes maximum) se télécharge en vidéo WebM.</p>
    </Modal>
  );
}

// ── Ban d'un joueur ──

function BanDialog({ player, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [hours, setHours] = useState(0);
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={`Bannir ${player.name}`} onClose={onClose}>
      <label className="block text-xs text-text-muted mb-1">Raison (affichée au joueur)</label>
      <input value={reason} onChange={(e) => setReason(e.target.value)} className={`${inputCls} mb-3`} placeholder="Ex : menu de triche" autoFocus />
      <label className="block text-xs text-text-muted mb-1">Durée</label>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {DURATIONS.map((d) => (
          <button key={d.hours} onClick={() => setHours(d.hours)}
            className={`px-2.5 py-1 text-xs border ${hours === d.hours ? "bg-red-600 border-red-500 text-white" : "bg-white/5 border-white/10 text-text-secondary"}`}>{d.label}</button>
        ))}
      </div>
      <p className="text-[11px] text-text-muted mb-4">Le ban couvre ses identifiants (licence, Discord, Steam…) et son matériel : un nouveau compte sur le même PC est refusé.</p>
      <div className="flex justify-end gap-2">
        <Btn onClick={onClose}>Annuler</Btn>
        <Btn variant="primary" disabled={busy} onClick={async () => { setBusy(true); await onConfirm(reason, hours); setBusy(false); }}><Ban className="w-4 h-4" />Bannir</Btn>
      </div>
    </Modal>
  );
}

// ── Onglets ──

function OverviewTab({ overview, onTab }) {
  if (!overview) return <p className="text-text-muted text-sm">Chargement…</p>;
  const s = overview.stats;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <StatTile label="Joueurs en ligne" value={overview.server.players_count || 0} icon={Users} hint={overview.server.online ? "serveur en ligne" : "serveur hors ligne"} />
        <StatTile label="Détections (24 h)" value={s.detections_24h} icon={ShieldAlert} hint={`${s.detections_7d} sur 7 jours`} />
        <StatTile label="Expulsions (24 h)" value={s.kicks_24h} icon={LogOut} />
        <StatTile label="Bans (24 h)" value={s.bans_24h} icon={Ban} hint={`${s.bans_active} ban(s) actif(s)`} />
        <StatTile label="Modules actifs" value={`${s.modules_enabled}/${s.modules_total}`} icon={Shield} />
        <button onClick={() => onTab("bans")} className="text-left" data-testid="tile-wave">
          <StatTile label="Vague en attente" value={overview.wave?.pending || 0} icon={Waves} tone={overview.wave?.pending ? "text-fuchsia-300" : "text-white"}
            hint={(overview.wave?.hour ?? -1) >= 0 ? `auto à ${overview.wave.hour} h` : "vague manuelle"} />
        </button>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title="Activité" icon={Activity} className="lg:col-span-2"><DailyChart days={overview.daily} /></Panel>
        <Panel title="Détections fréquentes (7 jours)" icon={Flame}>
          {overview.by_module.length === 0 ? <p className="text-sm text-text-muted">Aucune détection cette semaine.</p> : (
            <ul className="space-y-2.5">
              {overview.by_module.map((m) => {
                const max = overview.by_module[0].count;
                return (
                  <li key={m.module}>
                    <div className="flex justify-between text-xs mb-1"><span className="text-text-secondary">{m.label}</span><span className="text-white">{m.count}</span></div>
                    <div className="h-1.5 bg-white/5"><div className="h-full rounded-r-[4px]" style={{ width: `${(m.count / max) * 100}%`, background: "#3987e5" }} /></div>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>
      </div>
      <Panel title="Dernières détections" icon={ShieldAlert} actions={<button onClick={() => onTab("detections")} className="text-xs text-text-muted hover:text-white">Tout voir</button>}>
        {overview.recent.length === 0 ? <p className="text-sm text-text-muted">Rien à signaler pour le moment.</p> : (
          <ul className="divide-y divide-border-default">
            {overview.recent.map((d) => (
              <li key={d.id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="text-xs text-text-muted w-24">{fmtDate(d.at)}</span>
                <span className="font-medium">{d.player?.name}</span>
                <span className="text-text-secondary flex-1 min-w-[160px]">{d.reason}</span>
                <ActionBadge action={d.action} test={d.test_mode} />
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

function PlayersTab({ botId, onWhitelist, rank }) {
  const canModerate = rank >= RANK.moderator;
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [banTarget, setBanTarget] = useState(null);
  const [live, setLive] = useState(null);
  const [history, setHistory] = useState(null);
  const [frozen, setFrozen] = useState({});
  const [shot, setShot] = useState(null);

  const load = useCallback(() => ac.get(`/${botId}/players`).then(setData).catch(() => {}), [botId]);
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);

  const act = async (player, action, extra = {}) => {
    try {
      const res = await ac.post(`/${botId}/players/${player.src}/action`, { action, ...extra });
      return res;
    } catch (err) {
      toast.error(errorText(err, "Action impossible"));
      return null;
    }
  };

  const screenshot = async (player) => {
    const res = await act(player, "screenshot");
    if (!res) return;
    toast.info(`Capture demandée à ${player.name}…`);
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const cmd = await ac.get(`/${botId}/commands/${res.command_id}`);
        if (cmd.status === "done" && cmd.screenshot_id) { clearInterval(timer); setShot({ id: cmd.screenshot_id, player }); }
        else if (cmd.status === "failed" || cmd.status === "expired" || tries > 20) { clearInterval(timer); toast.error(cmd.result?.message || "Pas de capture reçue"); }
      } catch { clearInterval(timer); }
    }, 1000);
  };

  const openLive = async (player) => {
    try {
      const res = await ac.post(`/${botId}/live`, { src: player.src });
      setLive({ session: res, player });
    } catch (err) { toast.error(errorText(err, "Direct impossible")); }
  };

  const players = (data?.players || []).filter((p) => !q || `${p.name} ${p.src} ${(p.ids || []).join(" ")}`.toLowerCase().includes(q.toLowerCase()));

  if (data && !data.server.online) {
    return <Panel><p className="text-sm text-text-muted">Le serveur est hors ligne : démarrez-le (ensure vantaguard) pour voir les joueurs.</p></Panel>;
  }
  return (
    <Panel title={`Joueurs en ligne (${data?.players?.length ?? "…"})`} icon={Users}
      actions={<div className="relative"><Search className="w-3.5 h-3.5 absolute left-2 top-2 text-text-muted" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, ID, identifiant" className="bg-black/40 border border-white/10 pl-7 pr-2 py-1 text-xs w-44 sm:w-60" /></div>}>
      {players.length === 0 ? <p className="text-sm text-text-muted">{data ? "Aucun joueur." : "Chargement…"}</p> : (
        <div className="overflow-x-auto -mx-4">
          <table className="w-full text-sm min-w-[940px]">
            <thead><tr className="text-left text-xs text-text-muted"><th className="px-4 py-2">ID</th><th>Joueur</th><th>Identifiants</th><th>Ping</th><th>Connecté</th><th>Détections 30 j</th><th>Confiance</th><th className="text-right px-4">Actions</th></tr></thead>
            <tbody>
              {players.map((p) => {
                const ids = idsOf(p.ids);
                return (
                  <tr key={p.src} className="border-t border-white/10 align-middle" data-testid={`player-row-${p.src}`}>
                    <td className="px-4 py-2 font-mono text-text-secondary">{p.src}</td>
                    <td><div className="font-medium">{p.name}</div>{p.bypass && <span className="text-[10px] text-emerald-300">liste blanche</span>}</td>
                    <td className="text-[11px] font-mono text-text-muted max-w-[220px]">
                      {ids.license && <div className="truncate">license:{ids.license.slice(0, 12)}…</div>}
                      {ids.discord && <div>discord:{ids.discord}</div>}
                      {ids.steam && <div>steam:{ids.steam}</div>}
                    </td>
                    <td className="text-text-secondary">{p.ping} ms</td>
                    <td className="text-text-secondary">{since(p.joined_at)}</td>
                    <td>{p.detections_30d ? <span className="text-amber-300">{p.detections_30d}</span> : <span className="text-text-muted">0</span>}</td>
                    <td><TrustBadge score={p.trust} label={p.trust_label} /></td>
                    <td className="px-4">
                      <div className="flex justify-end gap-1">
                        {canModerate && <>
                        <Btn title="Écran en direct" onClick={() => openLive(p)} data-testid={`live-${p.src}`}><Monitor className="w-4 h-4" /></Btn>
                        <Btn title="Capture d'écran" onClick={() => screenshot(p)}><Camera className="w-4 h-4" /></Btn>
                        <Btn title="Envoyer un message" onClick={async () => { const text = window.prompt(`Message pour ${p.name}`); if (text && await act(p, "message", { text })) toast.success("Message affiché en jeu"); }}><MessageSquare className="w-4 h-4" /></Btn>
                        <Btn title={frozen[p.src] ? "Dégeler" : "Geler"} onClick={async () => { const action = frozen[p.src] ? "unfreeze" : "freeze"; if (await act(p, action)) { setFrozen({ ...frozen, [p.src]: !frozen[p.src] }); toast.success(action === "freeze" ? "Joueur gelé" : "Joueur dégelé"); } }}>
                          <Snowflake className={`w-4 h-4 ${frozen[p.src] ? "text-sky-300" : ""}`} />
                        </Btn>
                        </>}
                        {canModerate && <Btn title="Supprimer ses entités (véhicules, PNJ, objets qu'il a fait apparaître)" onClick={async () => { if (!window.confirm(`Supprimer tout ce que ${p.name} a fait apparaître ?`)) return; if (await act(p, "clean")) toast.success("Nettoyage demandé au serveur"); }} data-testid={`clean-${p.src}`}><Trash2 className="w-4 h-4" /></Btn>}
                        <Btn title="Fiche du joueur" onClick={async () => { const key = p.ids?.find((i) => i.startsWith("license:")) || p.name; try { setHistory({ player: p, data: await ac.get(`/${botId}/players/history`, { q: key }) }); } catch (err) { toast.error(errorText(err, "Fiche indisponible")); } }}><History className="w-4 h-4" /></Btn>
                        {rank >= RANK.admin && <Btn title="Ajouter à la liste blanche" onClick={() => onWhitelist(p)}><UserCheck className="w-4 h-4" /></Btn>}
                        {canModerate && <>
                        <Btn title="Expulser" variant="danger" onClick={async () => { const reason = window.prompt(`Raison de l'expulsion de ${p.name}`, "Décision du staff"); if (reason !== null && await act(p, "kick", { reason })) toast.success(`${p.name} expulsé`); }}><LogOut className="w-4 h-4" /></Btn>
                        <Btn title="Bannir" variant="danger" onClick={() => setBanTarget(p)}><Ban className="w-4 h-4" /></Btn>
                        </>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {banTarget && (
        <BanDialog player={banTarget} onClose={() => setBanTarget(null)} onConfirm={async (reason, hours) => {
          const res = await act(banTarget, "ban", { reason, ban_hours: hours });
          if (res) { toast.success(`${banTarget.name} banni (${res.ban?.code})`); setBanTarget(null); load(); }
        }} />
      )}
      {live && <LiveViewer botId={botId} session={live.session} player={live.player} onClose={() => setLive(null)} onScreenshot={() => screenshot(live.player)} />}
      {shot && (
        <Modal title={`Capture : ${shot.player.name}`} onClose={() => setShot(null)} wide>
          <AuthImage botId={botId} shotId={shot.id} className="w-full aspect-video" />
        </Modal>
      )}
      {history && (
        <Modal title={`Fiche : ${history.player.name}`} onClose={() => setHistory(null)} wide>
          <PlayerHistory data={history.data} />
        </Modal>
      )}
    </Panel>
  );
}

function PlayerHistory({ data }) {
  return (
    <div className="grid md:grid-cols-3 gap-4 text-sm">
      <div>
        <h4 className="text-xs text-text-muted mb-2">Détections ({data.detections.length})</h4>
        <ul className="space-y-2">{data.detections.slice(0, 30).map((d) => <li key={d.id} className="border border-white/10 p-2"><div className="text-xs text-text-muted">{fmtDate(d.at)}</div><div>{d.reason}</div><ActionBadge action={d.action} test={d.test_mode} /></li>)}</ul>
      </div>
      <div>
        <h4 className="text-xs text-text-muted mb-2">Bans ({data.bans.length})</h4>
        <ul className="space-y-2">{data.bans.map((b) => <li key={b.code} className="border border-white/10 p-2"><div className="font-mono text-xs">{b.code} {b.active ? <span className="text-red-300">actif</span> : <span className="text-text-muted">levé</span>}</div><div>{b.reason}</div></li>)}</ul>
      </div>
      <div>
        <h4 className="text-xs text-text-muted mb-2">Connexions ({data.logs.length})</h4>
        <ul className="space-y-1">{data.logs.slice(0, 40).map((l) => <li key={l.id} className="text-xs text-text-secondary">{fmtDate(l.at)} · {LOG_TYPES[l.type] || l.type}{l.reason ? ` · ${l.reason}` : ""}</li>)}</ul>
      </div>
    </div>
  );
}

function DetectionsTab({ botId, catalog, rank }) {
  const [filters, setFilters] = useState({ module: "", action: "", status: "", q: "" });
  const [data, setData] = useState({ total: 0, items: [] });
  const [open, setOpen] = useState(null);
  const [shot, setShot] = useState(null);
  const [limit, setLimit] = useState(50);

  const load = useCallback(() => {
    const params = { limit };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    ac.get(`/${botId}/detections`, params).then(setData).catch(() => {});
  }, [botId, filters, limit]);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const mark = async (det, status) => {
    try {
      const res = await ac.patch(`/${botId}/detections/${det.id}`, { status });
      toast.success(status === "false_positive" ? (res.unbanned ? "Faux positif : le ban a été levé" : "Marqué comme faux positif") : "Triche confirmée");
      load();
    } catch (err) { toast.error(errorText(err, "Erreur")); }
  };

  return (
    <Panel title={`Détections (${data.total})`} icon={ShieldAlert}>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px]"><Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-text-muted" /><input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Joueur, raison ou identifiant (license:…)" className={`${inputCls} pl-7`} /></div>
        <select value={filters.module} onChange={(e) => setFilters({ ...filters, module: e.target.value })} className="bg-black/40 border border-white/10 px-2 py-1.5 text-sm">
          <option value="">Toutes les détections</option>
          {(catalog?.modules || []).map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          <option value="custom">Signalé par un script</option>
        </select>
        <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} className="bg-black/40 border border-white/10 px-2 py-1.5 text-sm">
          <option value="">Toutes les sanctions</option>
          {Object.entries(ACTIONS).map(([k, a]) => <option key={k} value={k}>{a.done}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="bg-black/40 border border-white/10 px-2 py-1.5 text-sm">
          <option value="">Tous les statuts</option><option value="new">À vérifier</option><option value="confirmed">Confirmées</option><option value="false_positive">Faux positifs</option>
        </select>
      </div>
      {data.items.length === 0 ? <p className="text-sm text-text-muted">Aucune détection.</p> : (
        <ul className="divide-y divide-border-default -mx-4">
          {data.items.map((d) => (
            <li key={d.id} className="px-4 py-2.5">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <span className="text-xs text-text-muted w-24 shrink-0">{fmtDate(d.at)}</span>
                <span className="font-medium">{d.player?.name} <span className="text-xs text-text-muted font-mono">#{d.player?.src}</span></span>
                <span className="text-sm text-text-secondary flex-1 min-w-[200px]">{d.reason}</span>
                <ActionBadge action={d.action} test={d.test_mode} />
                {d.ban_code && <span className="font-mono text-[11px] text-red-300">{d.ban_code}</span>}
                {d.status === "false_positive" && <span className="text-[11px] text-emerald-300">faux positif</span>}
                {d.status === "confirmed" && <span className="text-[11px] text-red-300">confirmée</span>}
                {d.screenshot_id && <AuthImage botId={botId} shotId={d.screenshot_id} className="w-16 h-9 border border-white/10" onClick={() => setShot(d)} />}
                <button onClick={() => setOpen(open === d.id ? null : d.id)} className="text-xs text-text-muted hover:text-white underline decoration-dotted">{open === d.id ? "Masquer" : "Détails"}</button>
              </div>
              {open === d.id && (
                <div className="mt-2 grid md:grid-cols-2 gap-3 bg-black/30 border border-white/10 p-3 text-xs">
                  <div>
                    <div className="text-text-muted mb-1">Détails</div>
                    {Object.entries(d.details || {}).map(([k, v]) => <div key={k}><span className="text-text-muted">{k.replace(/_/g, " ")} :</span> <span className="font-mono">{String(v)}</span></div>)}
                    <div className="text-text-muted mt-2 mb-1">Identifiants</div>
                    {(d.player?.ids || []).map((i) => <div key={i} className="font-mono break-all">{i}</div>)}
                  </div>
                  {rank >= RANK.moderator && <div className="flex flex-col gap-2 items-start">
                    <Btn variant="danger" onClick={() => mark(d, "confirmed")}><Check className="w-4 h-4" />Confirmer la triche</Btn>
                    <Btn variant="success" onClick={() => mark(d, "false_positive")}><ShieldCheck className="w-4 h-4" />Faux positif{d.ban_code ? " (lève le ban)" : ""}</Btn>
                    <p className="text-[11px] text-text-muted">Beaucoup de faux positifs sur une détection ? Passez-la en « Journaliser » ou montez ses seuils dans Modules.</p>
                  </div>}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {data.total > data.items.length && <div className="mt-3 text-center"><Btn onClick={() => setLimit(limit + 50)}>Afficher plus</Btn></div>}
      {shot && (
        <Modal title={`${shot.reason} : ${shot.player?.name}`} onClose={() => setShot(null)} wide>
          <AuthImage botId={botId} shotId={shot.screenshot_id} className="w-full aspect-video" />
        </Modal>
      )}
    </Panel>
  );
}

function WavePanel({ botId, rank, wave, onDone }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { ac.get(`/${botId}/bans`, { wave: true, limit: 100 }).then((r) => setItems(r.items)).catch(() => {}); }, [botId]);
  useEffect(() => { load(); }, [load, wave?.pending]);
  const run = async () => {
    if (!window.confirm(`Bannir maintenant les ${items.length} joueur(s) en attente ?`)) return;
    setBusy(true);
    try {
      const res = await ac.post(`/${botId}/waves/run`);
      toast.success(`Vague lancée : ${res.count} joueur(s) banni(s)`);
      load(); onDone();
    } catch (err) { toast.error(errorText(err, "Vague impossible")); }
    setBusy(false);
  };
  const cancel = async (b) => {
    if (!window.confirm(`Annuler le ban en attente de ${b.name || b.code} ?`)) return;
    try { await ac.del(`/${botId}/bans/${b.code}`); toast.success("Ban en attente annulé"); load(); onDone(); } catch (err) { toast.error(errorText(err, "Erreur")); }
  };
  const hour = wave?.hour ?? -1;
  return (
    <Panel title={`Vague de bans (${items.length} en attente)`} icon={Waves}
      actions={rank >= RANK.moderator && <Btn variant="primary" onClick={run} disabled={busy || items.length === 0} data-testid="wave-run"><Waves className="w-4 h-4" />{busy ? "En cours…" : "Lancer la vague"}</Btn>}>
      <p className="text-xs text-text-muted mb-3">
        Les modules réglés sur « Vague » ne bannissent pas tout de suite : le tricheur reste en jeu sans savoir qu'il a été repéré, puis tout le monde est banni en même temps. Il est plus difficile pour les vendeurs de menus de savoir quelle détection les a eus.
        {" "}{hour >= 0 ? `Vague automatique chaque jour à ${hour} h (heure de Paris).` : "Vague manuelle (heure automatique réglable dans Paramètres)."}
        {wave?.last_at ? ` Dernière vague : ${fmtDate(wave.last_at)}.` : ""}
      </p>
      {items.length === 0 ? <p className="text-sm text-text-muted">Personne en attente.</p> : (
        <ul className="divide-y divide-border-default -mx-4" data-testid="wave-list">
          {items.map((b) => (
            <li key={b.code} className="px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="font-mono text-xs w-24">{b.code}</span>
              <span className="font-medium">{b.name}</span>
              <span className="text-text-secondary flex-1 min-w-[180px]">{b.reason}</span>
              <span className="text-xs text-text-muted">{fmtDate(b.created_at)}</span>
              {rank >= RANK.moderator && <Btn variant="success" onClick={() => cancel(b)}>Annuler</Btn>}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function BansTab({ botId, rank, wave, onChange }) {
  const canModerate = rank >= RANK.moderator;
  const [data, setData] = useState({ total: 0, items: [] });
  const [q, setQ] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [form, setForm] = useState({ identifiers: "", name: "", reason: "", ban_hours: 0 });
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    const params = { limit: 100 };
    if (q) params.q = q;
    if (onlyActive) params.active = true;
    ac.get(`/${botId}/bans`, params).then(setData).catch(() => {});
  }, [botId, q, onlyActive]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try {
      const ban = await ac.post(`/${botId}/bans`, { ...form, identifiers: form.identifiers.split(/[\s,;]+/).filter(Boolean) });
      toast.success(`Ban ${ban.code} ajouté`);
      setForm({ identifiers: "", name: "", reason: "", ban_hours: 0 });
      setAdding(false);
      load();
    } catch (err) { toast.error(errorText(err, "Ban impossible")); }
  };

  return (
    <div className="space-y-4">
      <WavePanel botId={botId} rank={rank} wave={wave} onDone={() => { load(); onChange(); }} />
      <Panel title={`Bans (${data.total})`} icon={Ban} actions={canModerate && <Btn variant="primary" onClick={() => setAdding(!adding)}><Plus className="w-4 h-4" />Bannir par identifiant</Btn>}>
        {adding && (
          <div className="mb-4 bg-black/30 border border-white/10 p-3 grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs text-text-muted mb-1">Identifiants (un par ligne)</label>
              <textarea value={form.identifiers} onChange={(e) => setForm({ ...form, identifiers: e.target.value })} rows={3} className={`${inputCls} font-mono`} placeholder={"license:1a2b3c…\ndiscord:123456789012345678\nsteam:110000100000000"} />
            </div>
            <div><label className="block text-xs text-text-muted mb-1">Pseudo (pour s'y retrouver)</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
            <div><label className="block text-xs text-text-muted mb-1">Raison</label><input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className={inputCls} /></div>
            <div className="md:col-span-2 flex flex-wrap items-center gap-1.5">
              {DURATIONS.map((d) => <button key={d.hours} onClick={() => setForm({ ...form, ban_hours: d.hours })} className={`px-2.5 py-1 text-xs border ${form.ban_hours === d.hours ? "bg-red-600 border-red-500" : "bg-white/5 border-white/10 text-text-secondary"}`}>{d.label}</button>)}
              <div className="flex-1" />
              <Btn variant="primary" onClick={add} disabled={!form.identifiers.trim()}><Ban className="w-4 h-4" />Bannir</Btn>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-3 items-center mb-3">
          <div className="relative flex-1 min-w-[200px]"><Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-text-muted" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code, pseudo, raison ou identifiant" className={`${inputCls} pl-7`} /></div>
          <label className="flex items-center gap-2 text-xs text-text-secondary"><Toggle checked={onlyActive} onChange={setOnlyActive} label="Bans actifs seulement" />Actifs seulement</label>
        </div>
        {data.items.length === 0 ? <p className="text-sm text-text-muted">Aucun ban.</p> : (
          <div className="overflow-x-auto -mx-4">
            <table className="w-full text-sm min-w-[760px]">
              <thead><tr className="text-left text-xs text-text-muted"><th className="px-4 py-2">Code</th><th>Joueur</th><th>Raison</th><th>Par</th><th>Le</th><th>Fin</th><th className="px-4 text-right">Action</th></tr></thead>
              <tbody>
                {data.items.map((b) => (
                  <tr key={b.code} className="border-t border-white/10">
                    <td className="px-4 py-2 font-mono text-xs">{b.code}</td>
                    <td><div>{b.name}</div><div className="text-[10px] text-text-muted">{b.ids?.length || 0} identifiant(s), {b.tokens_count} matériel(s)</div></td>
                    <td className="text-text-secondary max-w-[220px]">{b.reason}</td>
                    <td className="text-xs text-text-muted">{b.by === "auto" ? "VantaGuard" : (b.by_user || b.by)}</td>
                    <td className="text-xs text-text-muted">{fmtDate(b.created_at)}</td>
                    <td className="text-xs">{b.wave_pending ? <span className="text-fuchsia-300">prochaine vague</span> : !b.active ? <span className="text-text-muted">levé</span> : b.expired ? <span className="text-text-muted">expiré</span> : b.expires_at ? fmtDate(b.expires_at) : <span className="text-red-300">définitif</span>}</td>
                    <td className="px-4 text-right">
                      {canModerate && b.active && !b.expired && <Btn variant="success" onClick={async () => { if (!window.confirm(`Lever le ban ${b.code} ?`)) return; try { await ac.del(`/${botId}/bans/${b.code}`); toast.success("Ban levé"); load(); } catch (err) { toast.error(errorText(err, "Erreur")); } }}>Lever</Btn>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

function CapturesTab({ botId }) {
  const [data, setData] = useState({ total: 0, items: [] });
  const [shot, setShot] = useState(null);
  useEffect(() => { ac.get(`/${botId}/screenshots`, { limit: 60 }).then(setData).catch(() => {}); }, [botId]);
  return (
    <Panel title={`Captures d'écran (${data.total})`} icon={Camera}>
      {data.items.length === 0 ? <p className="text-sm text-text-muted">Aucune capture. Elles sont prises avant chaque sanction (réglable par module) ou depuis l'onglet Joueurs.</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {data.items.map((s) => (
            <figure key={s.id} className="border border-white/10 bg-black/30">
              <AuthImage botId={botId} shotId={s.id} className="w-full aspect-video" onClick={() => setShot(s)} />
              <figcaption className="p-2 text-xs"><div className="font-medium truncate">{s.player?.name || "Joueur"}</div><div className="text-text-muted truncate">{fmtDate(s.created_at)} · {s.reason || (s.detection_id ? "Détection" : "Capture")}</div></figcaption>
            </figure>
          ))}
        </div>
      )}
      {shot && <Modal title={`${shot.player?.name || "Capture"} · ${fmtDate(shot.created_at)}`} onClose={() => setShot(null)} wide><AuthImage botId={botId} shotId={shot.id} className="w-full aspect-video" /></Modal>}
    </Panel>
  );
}

function ModulesTab({ draft, setDraft, catalog }) {
  const groups = useMemo(() => {
    const out = {};
    (catalog?.modules || []).forEach((m) => { (out[m.category] = out[m.category] || []).push(m); });
    return out;
  }, [catalog]);
  const update = (key, patch) => setDraft({ ...draft, modules: { ...draft.modules, [key]: { ...draft.modules[key], ...patch } } });
  const updateParam = (key, name, value) => update(key, { params: { ...draft.modules[key].params, [name]: value } });
  return (
    <div className="space-y-4">
      {draft.settings.test_mode && (
        <div className="flex items-start gap-2 bg-violet-500/10 border border-violet-500/30 px-3 py-2 text-sm text-violet-100"><FlaskConical className="w-4 h-4 mt-0.5 shrink-0" />Mode test actif : les actions ci-dessous sont journalisées sans sanction. Désactivez-le dans Paramètres quand les réglages vous conviennent.</div>
      )}
      {Object.entries(groups).map(([category, mods]) => (
        <Panel key={category} title={category} icon={Shield}>
          <div className="grid lg:grid-cols-2 gap-3">
            {mods.map((m) => {
              const cfg = draft.modules[m.key];
              if (!cfg) return null;
              return (
                <div key={m.key} className={`border p-3 ${cfg.enabled ? "border-white/10 bg-black/20" : "border-white/10/50 opacity-60"}`} data-testid={`module-${m.key}`}>
                  <div className="flex items-start gap-3">
                    <Toggle checked={cfg.enabled} onChange={(v) => update(m.key, { enabled: v })} label={`Activer ${m.label}`} testId={`toggle-${m.key}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2"><span className="font-medium text-sm">{m.label}</span><span className="text-[10px] px-1.5 py-0.5 bg-white/5 border border-white/10 text-text-muted">{m.side}</span></div>
                      <p className="text-xs text-text-muted mt-0.5">{m.description}</p>
                    </div>
                  </div>
                  {cfg.enabled && (
                    <div className="mt-3 space-y-2.5 pl-[52px]">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(ACTIONS).map(([k, a]) => {
                          const Icon = a.icon;
                          return <button key={k} onClick={() => update(m.key, { action: k })} data-testid={`action-${m.key}-${k}`}
                            className={`inline-flex items-center gap-1 px-2 py-1 text-xs border ${cfg.action === k ? a.cls : "bg-white/5 border-white/10 text-text-muted"}`}><Icon className="w-3 h-3" />{a.label}</button>;
                        })}
                      </div>
                      {cfg.action === "ban" && (
                        <label className="flex items-center gap-2 text-xs text-text-secondary">Durée du ban
                          <select value={cfg.ban_hours} onChange={(e) => update(m.key, { ban_hours: Number(e.target.value) })} className="bg-black/40 border border-white/10 px-2 py-1 text-xs">
                            {DURATIONS.map((d) => <option key={d.hours} value={d.hours}>{d.label}</option>)}
                            {!DURATIONS.some((d) => d.hours === Number(cfg.ban_hours)) && <option value={cfg.ban_hours}>{durationLabel(cfg.ban_hours)}</option>}
                          </select>
                        </label>
                      )}
                      <label className="flex items-center gap-2 text-xs text-text-secondary"><Toggle checked={cfg.screenshot} onChange={(v) => update(m.key, { screenshot: v })} label="Capture d'écran" />Capture d'écran avant la sanction</label>
                      {Object.entries(m.params).map(([name, spec]) => (
                        <label key={name} className="flex items-center justify-between gap-3 text-xs text-text-secondary">
                          <span>{spec.label}</span>
                          {spec.type === "bool" ? (
                            <Toggle checked={!!cfg.params[name]} onChange={(v) => updateParam(m.key, name, v)} label={spec.label} />
                          ) : (
                            <input type="number" value={cfg.params[name] ?? spec.default} min={spec.min} max={spec.max} step={spec.type === "float" ? 0.5 : 1}
                              onChange={(e) => updateParam(m.key, name, e.target.value === "" ? "" : Number(e.target.value))}
                              className="w-24 bg-black/40 border border-white/10 px-2 py-1 text-xs text-right" />
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>
      ))}
    </div>
  );
}

function ListsTab({ draft, setDraft, catalog }) {
  const [ident, setIdent] = useState("");
  const [note, setNote] = useState("");
  const setList = (key, values) => setDraft({ ...draft, lists: { ...draft.lists, [key]: values } });
  const addWhitelist = () => {
    const value = ident.trim().toLowerCase();
    if (!value.includes(":")) { toast.error("Format attendu : license:…, discord:…, steam:…"); return; }
    if (draft.whitelist.some((w) => w.identifier === value)) return;
    setDraft({ ...draft, whitelist: [...draft.whitelist, { identifier: value, note: note.trim() }] });
    setIdent(""); setNote("");
  };
  return (
    <div className="space-y-4">
      <Panel title="Liste blanche (staff exempté de toutes les détections)" icon={UserCheck}>
        <p className="text-xs text-text-muted mb-3">Les bans s'appliquent toujours. Vous pouvez aussi donner la permission <code className="text-emerald-200">vantaguard.bypass</code> dans server.cfg (ex : add_ace group.admin vantaguard.bypass allow).</p>
        <ul className="divide-y divide-border-default mb-3">
          {draft.whitelist.length === 0 && <li className="text-sm text-text-muted py-1">Personne.</li>}
          {draft.whitelist.map((w) => (
            <li key={w.identifier} className="flex items-center gap-3 py-1.5 text-sm">
              <span className="font-mono text-xs flex-1 break-all">{w.identifier}</span><span className="text-text-muted text-xs">{w.note}</span>
              <button onClick={() => setDraft({ ...draft, whitelist: draft.whitelist.filter((x) => x.identifier !== w.identifier) })} className="text-text-muted hover:text-red-300 p-1" aria-label="Retirer"><Trash2 className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2">
          <input value={ident} onChange={(e) => setIdent(e.target.value)} placeholder="license:… ou discord:…" className="flex-1 min-w-[200px] bg-black/40 border border-white/10 px-2 py-1.5 text-sm font-mono" />
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (ex : fondateur)" className="w-48 bg-black/40 border border-white/10 px-2 py-1.5 text-sm" />
          <Btn onClick={addWhitelist} disabled={!ident.trim()}><Plus className="w-4 h-4" />Ajouter</Btn>
        </div>
      </Panel>
      <div className="grid lg:grid-cols-2 gap-4">
        {Object.entries(catalog?.lists || {}).map(([key, meta]) => (
          <Panel key={key} title={meta.label} icon={List}>
            <ChipEditor values={draft.lists[key] || []} onChange={(v) => setList(key, v)} placeholder={meta.hint} numeric={key === "explosions"} testId={`list-${key}`} />
          </Panel>
        ))}
      </div>
    </div>
  );
}

function ScriptsTab({ botId, draft, setDraft }) {
  const [resources, setResources] = useState(null);
  const [ev, setEv] = useState({ name: "", max_per_minute: 30, action: "kick", note: "" });
  useEffect(() => { ac.get(`/${botId}/resources`).then(setResources).catch(() => setResources({ resources: [] })); }, [botId]);
  const trusted = new Set(draft.trusted_resources || []);
  const toggleTrusted = (name) => {
    const next = new Set(trusted);
    if (next.has(name)) next.delete(name); else next.add(name);
    setDraft({ ...draft, trusted_resources: [...next] });
  };
  const protectedEvents = draft.events.protected || [];
  const addEvent = () => {
    if (!ev.name.trim() || protectedEvents.some((p) => p.name === ev.name.trim())) return;
    setDraft({ ...draft, events: { ...draft.events, protected: [...protectedEvents, { ...ev, name: ev.name.trim() }] } });
    setEv({ name: "", max_per_minute: 30, action: "kick", note: "" });
  };
  return (
    <div className="space-y-4">
      <Panel title="Événements protégés de vos scripts" icon={Code2}>
        <p className="text-xs text-text-muted mb-3">Ajoutez les événements serveur sensibles de vos scripts (argent, objets, salaires…). Au-delà du nombre d'appels par minute et par joueur, VantaGuard applique l'action choisie. Vos scripts ne sont pas modifiés.</p>
        <div className="overflow-x-auto -mx-4 mb-3">
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="text-left text-xs text-text-muted"><th className="px-4 py-1.5">Événement</th><th>Max / minute</th><th>Action</th><th>Note</th><th /></tr></thead>
            <tbody>
              {protectedEvents.length === 0 && <tr><td colSpan={5} className="px-4 py-2 text-text-muted text-sm">Aucun événement protégé.</td></tr>}
              {protectedEvents.map((p, i) => (
                <tr key={p.name} className="border-t border-white/10">
                  <td className="px-4 py-1.5 font-mono text-xs">{p.name}</td>
                  <td><input type="number" min={1} value={p.max_per_minute} onChange={(e) => { const next = [...protectedEvents]; next[i] = { ...p, max_per_minute: Number(e.target.value) }; setDraft({ ...draft, events: { ...draft.events, protected: next } }); }} className="w-20 bg-black/40 border border-white/10 px-2 py-1 text-xs" /></td>
                  <td><select value={p.action} onChange={(e) => { const next = [...protectedEvents]; next[i] = { ...p, action: e.target.value }; setDraft({ ...draft, events: { ...draft.events, protected: next } }); }} className="bg-black/40 border border-white/10 px-2 py-1 text-xs">{Object.entries(ACTIONS).map(([k, a]) => <option key={k} value={k}>{a.label}</option>)}</select></td>
                  <td className="text-xs text-text-muted">{p.note}</td>
                  <td className="pr-4 text-right"><button onClick={() => setDraft({ ...draft, events: { ...draft.events, protected: protectedEvents.filter((x) => x.name !== p.name) } })} className="text-text-muted hover:text-red-300 p-1" aria-label="Retirer"><Trash2 className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-2">
          <input value={ev.name} onChange={(e) => setEv({ ...ev, name: e.target.value })} placeholder="esx_banque:retirer" className="flex-1 min-w-[200px] bg-black/40 border border-white/10 px-2 py-1.5 text-sm font-mono" />
          <input type="number" min={1} value={ev.max_per_minute} onChange={(e) => setEv({ ...ev, max_per_minute: Number(e.target.value) })} className="w-24 bg-black/40 border border-white/10 px-2 py-1.5 text-sm" title="Appels maximum par minute" />
          <select value={ev.action} onChange={(e) => setEv({ ...ev, action: e.target.value })} className="bg-black/40 border border-white/10 px-2 py-1.5 text-sm">{Object.entries(ACTIONS).map(([k, a]) => <option key={k} value={k}>{a.label}</option>)}</select>
          <input value={ev.note} onChange={(e) => setEv({ ...ev, note: e.target.value })} placeholder="Note" className="w-40 bg-black/40 border border-white/10 px-2 py-1.5 text-sm" />
          <Btn onClick={addEvent} disabled={!ev.name.trim()}><Plus className="w-4 h-4" />Protéger</Btn>
        </div>
      </Panel>
      <Panel title="Pièges à menus" icon={ShieldAlert}>
        <p className="text-xs text-text-muted mb-3">Événements qu'aucun de vos scripts n'utilise mais que les menus de triche déclenchent. Un piège n'est posé que si la ressource qui porte ce nom (avant les « : ») n'existe pas sur votre serveur, pour éviter tout faux positif. Déclencher un piège = sanction du module « Événements protégés et pièges ».</p>
        <ChipEditor values={draft.events.honeypots || []} onChange={(v) => setDraft({ ...draft, events: { ...draft.events, honeypots: v } })} placeholder="ressource:evenement" testId="honeypots" />
      </Panel>
      <Panel title={`Ressources du serveur (${resources?.resources?.length ?? "…"})`} icon={List}>
        <p className="text-xs text-text-muted mb-3">Envoyées par le serveur à chaque signal. Une ressource « de confiance » n'est jamais signalée comme injectée ou arrêtée chez un joueur.</p>
        {!resources ? <p className="text-sm text-text-muted">Chargement…</p> : resources.resources.length === 0 ? <p className="text-sm text-text-muted">Le serveur ne s'est pas encore connecté.</p> : (
          <div className="flex flex-wrap gap-1.5">
            {resources.resources.map((r) => (
              <button key={r.name} onClick={() => toggleTrusted(r.name)} title={trusted.has(r.name) ? "De confiance (cliquer pour retirer)" : "Marquer de confiance"}
                className={`px-2 py-1 text-xs font-mono border ${trusted.has(r.name) ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" : "bg-white/5 border-white/10 text-text-secondary"}`}>
                {trusted.has(r.name) && <Check className="w-3 h-3 inline mr-1" />}{r.name}
              </button>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function WebhooksTab({ botId, draft, setDraft, catalog }) {
  const [testing, setTesting] = useState(null);
  const test = async (kind) => {
    setTesting(kind);
    try {
      await ac.post(`/${botId}/webhooks/test`, { kind, url: draft.webhooks[kind] });
      toast.success("Message de test envoyé sur Discord");
    } catch (err) { toast.error(errorText(err, "Test impossible")); }
    setTesting(null);
  };
  return (
    <Panel title="Webhooks Discord" icon={Webhook}>
      <p className="text-xs text-text-muted mb-4">Dans Discord : Paramètres du salon › Intégrations › Webhooks › Nouveau webhook › Copier l'URL. Un salon par catégorie, ou le même partout. Les captures sont jointes aux messages. Les URL restent sur le site : votre serveur de jeu ne les connaît pas.</p>
      <div className="space-y-3">
        {Object.entries(catalog?.webhooks || {}).map(([kind, label]) => (
          <div key={kind} className="grid md:grid-cols-[200px_1fr_auto] gap-2 items-center">
            <label className="text-sm text-text-secondary" htmlFor={`hook-${kind}`}>{label}</label>
            <input id={`hook-${kind}`} value={draft.webhooks[kind] || ""} onChange={(e) => setDraft({ ...draft, webhooks: { ...draft.webhooks, [kind]: e.target.value.trim() } })}
              placeholder="https://discord.com/api/webhooks/…" className={`${inputCls} font-mono text-xs`} />
            <Btn onClick={() => test(kind)} disabled={!draft.webhooks[kind] || testing === kind}>{testing === kind ? "Envoi…" : "Tester"}</Btn>
          </div>
        ))}
      </div>
    </Panel>
  );
}

const LOG_TYPES = { join: "Connexion", leave: "Départ", kick: "Expulsion", refused: "Connexion refusée", staff: "Action du staff en jeu" };

function LogsTab({ botId }) {
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ total: 0, items: [] });
  const [audit, setAudit] = useState([]);
  useEffect(() => {
    const params = { limit: 100 };
    if (type) params.type = type;
    if (q) params.q = q;
    ac.get(`/${botId}/logs`, params).then(setData).catch(() => {});
  }, [botId, type, q]);
  useEffect(() => { ac.get(`/${botId}/audit`).then((r) => setAudit(r.items)).catch(() => {}); }, [botId]);
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Panel title={`Connexions et départs (${data.total})`} icon={History} className="lg:col-span-2">
        <div className="flex flex-wrap gap-2 mb-3">
          <select value={type} onChange={(e) => setType(e.target.value)} className="bg-black/40 border border-white/10 px-2 py-1.5 text-sm"><option value="">Tout</option>{Object.entries(LOG_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pseudo ou identifiant" className="flex-1 min-w-[180px] bg-black/40 border border-white/10 px-2 py-1.5 text-sm" />
        </div>
        <ul className="divide-y divide-border-default text-sm">
          {data.items.length === 0 && <li className="text-text-muted py-1">Rien pour le moment.</li>}
          {data.items.map((l) => (
            <li key={l.id} className="py-1.5 flex flex-wrap gap-x-3"><span className="text-xs text-text-muted w-24">{fmtDate(l.at)}</span><span className="w-36 text-text-secondary">{LOG_TYPES[l.type]}</span><span className="font-medium">{l.player?.name}</span><span className="text-text-muted text-xs flex-1">{l.reason}</span></li>
          ))}
        </ul>
      </Panel>
      <Panel title="Actions sur le tableau de bord" icon={Settings}>
        <ul className="space-y-2 text-xs">
          {audit.length === 0 && <li className="text-text-muted">Aucune action.</li>}
          {audit.map((a) => <li key={a.id} className="border-b border-white/10 pb-1.5"><div className="text-text-muted">{fmtDate(a.at)} · {a.user_name}</div><div>{a.action}{a.details?.player ? ` : ${a.details.player}` : ""}{a.details?.code ? ` (${a.details.code})` : ""}</div></li>)}
        </ul>
      </Panel>
    </div>
  );
}

function SettingsTab({ botId, draft, setDraft, catalog, onReset }) {
  const st = draft.settings;
  const set = (patch) => setDraft({ ...draft, settings: { ...st, ...patch } });
  const req = new Set(st.required_identifiers || []);
  return (
    <div className="space-y-4">
      <Panel title="Mode test" icon={FlaskConical}>
        <label className="flex items-start gap-3 text-sm">
          <Toggle checked={st.test_mode} onChange={(v) => set({ test_mode: v })} label="Mode test" testId="toggle-test-mode" />
          <span><span className="font-medium">Journaliser sans sanctionner</span><span className="block text-xs text-text-muted">Conseillé les premiers jours : les détections arrivent ici et sur Discord, personne n'est expulsé ni banni. Vous ajustez les seuils, puis vous le désactivez.</span></span>
        </label>
      </Panel>
      <Panel title="Messages aux joueurs" icon={MessageSquare}>
        <label className="block text-xs text-text-muted mb-1">Message de ban (variables : {"{code}"}, {"{reason}"}, {"{expires}"})</label>
        <textarea rows={4} value={st.ban_message} onChange={(e) => set({ ban_message: e.target.value })} className={`${inputCls} mb-3`} />
        <label className="block text-xs text-text-muted mb-1">Message d'expulsion (variable : {"{reason}"})</label>
        <input value={st.kick_message} onChange={(e) => set({ kick_message: e.target.value })} className={`${inputCls} mb-3`} />
        <label className="block text-xs text-text-muted mb-1">Lien de contestation (Discord, formulaire…), ajouté au message de ban</label>
        <input value={st.appeal_url} onChange={(e) => set({ appeal_url: e.target.value })} placeholder="https://discord.gg/…" className={inputCls} />
      </Panel>
      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Connexion" icon={Shield}>
          <p className="text-xs text-text-muted mb-2">Identifiants obligatoires pour rejoindre le serveur :</p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {(catalog?.identifiers || []).filter((i) => i !== "ip").map((i) => (
              <button key={i} onClick={() => { const next = new Set(req); if (next.has(i)) next.delete(i); else next.add(i); set({ required_identifiers: [...next] }); }}
                className={`px-2 py-1 text-xs font-mono border ${req.has(i) ? "bg-red-600/20 border-red-500/40 text-white" : "bg-white/5 border-white/10 text-text-muted"}`}>{i}</button>
            ))}
          </div>
          <label className="block text-xs text-text-muted mb-1">Permission ACE d'exemption</label>
          <input value={st.bypass_ace} onChange={(e) => set({ bypass_ace: e.target.value })} className={`${inputCls} font-mono`} />
          <p className="text-[11px] text-text-muted mt-1">Exemption d'un seul module : {st.bypass_ace}.noclip, {st.bypass_ace}.teleport…</p>
        </Panel>
        <Panel title="Adresses IP et vie privée" icon={Eye}>
          {[["share_ip", "Envoyer l'IP des joueurs au site", "Nécessaire pour bannir par IP."],
            ["match_ip", "Bannir aussi par IP", "Attention : une IP peut être partagée (colocation, réseau public)."],
            ["mask_ip_in_webhooks", "Masquer la fin des IP sur Discord", "Ex : 82.64.x.x"]].map(([key, label, hint]) => (
            <label key={key} className="flex items-start gap-3 text-sm mb-3">
              <Toggle checked={!!st[key]} onChange={(v) => set({ [key]: v })} label={label} />
              <span>{label}<span className="block text-[11px] text-text-muted">{hint}</span></span>
            </label>
          ))}
          <label className="flex items-center justify-between text-sm">Conserver les détections (jours)
            <input type="number" min={7} max={365} value={st.detections_retention_days} onChange={(e) => set({ detections_retention_days: Number(e.target.value) })} className="w-20 bg-black/40 border border-white/10 px-2 py-1 text-xs text-right" />
          </label>
        </Panel>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Sanctions progressives" icon={TrendingUp}>
          <label className="flex items-start gap-3 text-sm mb-3">
            <Toggle checked={!!st.escalation} onChange={(v) => set({ escalation: v })} label="Sanctions progressives" testId="toggle-escalation" />
            <span>Durcir la sanction quand un joueur récidive<span className="block text-[11px] text-text-muted">Une détection « Journaliser » qui revient souvent devient une expulsion, et des expulsions répétées deviennent un ban. Les détections de connexion, réseau, VPN et chat ne sont pas concernées.</span></span>
          </label>
          {st.escalation && <div className="space-y-2">
          <label className="flex items-center justify-between gap-3 text-sm">Détections journalisées avant expulsion
            <input type="number" min={2} max={50} value={st.esc_logs} onChange={(e) => set({ esc_logs: Number(e.target.value) })} className="w-20 bg-black/40 border border-white/10 px-2 py-1 text-xs text-right" />
          </label>
          <label className="flex items-center justify-between gap-3 text-sm">… sur combien de minutes
            <input type="number" min={1} max={1440} value={st.esc_logs_minutes} onChange={(e) => set({ esc_logs_minutes: Number(e.target.value) })} className="w-20 bg-black/40 border border-white/10 px-2 py-1 text-xs text-right" />
          </label>
          <label className="flex items-center justify-between gap-3 text-sm">Expulsions avant ban
            <input type="number" min={2} max={20} value={st.esc_kicks} onChange={(e) => set({ esc_kicks: Number(e.target.value) })} className="w-20 bg-black/40 border border-white/10 px-2 py-1 text-xs text-right" />
          </label>
          <label className="flex items-center justify-between gap-3 text-sm">… sur combien d'heures
            <input type="number" min={1} max={720} value={st.esc_kicks_hours} onChange={(e) => set({ esc_kicks_hours: Number(e.target.value) })} className="w-20 bg-black/40 border border-white/10 px-2 py-1 text-xs text-right" />
          </label>
          <label className="flex items-center justify-between gap-3 text-sm">Durée de ce ban
            <select value={st.esc_ban_hours} onChange={(e) => set({ esc_ban_hours: Number(e.target.value) })} className="bg-black/40 border border-white/10 px-2 py-1 text-xs">
              {DURATIONS.map((d) => <option key={d.hours} value={d.hours}>{d.label}</option>)}
              {!DURATIONS.some((d) => d.hours === Number(st.esc_ban_hours)) && <option value={st.esc_ban_hours}>{durationLabel(st.esc_ban_hours)}</option>}
            </select>
          </label>
          </div>}
        </Panel>
        <Panel title="Vagues de bans" icon={Waves}>
          <p className="text-xs text-text-muted mb-3">Pour les modules réglés sur « Vague » : les tricheurs repérés sont bannis tous ensemble, plus tard. Ils ne savent pas quelle détection les a eus.</p>
          <label className="flex items-center justify-between gap-3 text-sm">Vague automatique
            <select value={st.wave_hour} onChange={(e) => set({ wave_hour: Number(e.target.value) })} className="bg-black/40 border border-white/10 px-2 py-1 text-xs" data-testid="wave-hour">
              <option value={-1}>Manuelle (bouton ou /vgwave)</option>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>Chaque jour à {h} h</option>)}
            </select>
          </label>
          <p className="text-[11px] text-text-muted mt-2">Heure de Paris. Le serveur doit être allumé à cette heure-là (sinon la vague part au prochain signal du serveur, le même jour).</p>
        </Panel>
      </div>
      <Panel title="Protection des entités" icon={ShieldCheck}>
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="space-y-3">
            <label className="flex items-start gap-3 text-sm">
              <Toggle checked={st.cleanup_on_sanction !== false} onChange={(v) => set({ cleanup_on_sanction: v })} label="Nettoyage après sanction" testId="toggle-cleanup" />
              <span>Supprimer ce que le tricheur a fait apparaître quand il est expulsé ou banni<span className="block text-[11px] text-text-muted">Véhicules, PNJ et objets de ses scripts, et les feux autour de lui. Aussi avec /vgclean et le bouton « Supprimer ses entités » de l'onglet Joueurs.</span></span>
            </label>
            <label className="flex items-start gap-3 text-sm">
              <Toggle checked={st.cleanup_on_detection !== false} onChange={(v) => set({ cleanup_on_detection: v })} label="Nettoyage à la détection" />
              <span>Supprimer tout de suite ses entités de la dernière minute<span className="block text-[11px] text-text-muted">Dès qu'une détection d'entités, d'explosions, de feux, de particules ou de projectiles le touche (sauf en mode test).</span></span>
            </label>
            <label className="flex items-center justify-between gap-3 text-sm">Entités supprimées à la sanction
              <select value={st.cleanup_minutes ?? 15} onChange={(e) => set({ cleanup_minutes: Number(e.target.value) })} className="bg-black/40 border border-white/10 px-2 py-1 text-xs">
                {[[5, "5 dernières minutes"], [15, "15 dernières minutes"], [60, "Dernière heure"], [0, "Toute la session"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          </div>
          <div>
            <p className="text-sm mb-1">Verrouillage des entités</p>
            <p className="text-[11px] text-text-muted mb-2">La protection la plus forte contre les menus qui font apparaître des véhicules, PNJ et objets : le serveur refuse les entités créées depuis le jeu des joueurs. Les scripts qui créent leurs véhicules côté serveur continuent de marcher ; testez vos garages et concessions avant de l'activer.</p>
            <div className="flex flex-wrap gap-1.5" data-testid="lockdown">
              {[["off", "Désactivé"], ["relaxed", "Scripts des joueurs bloqués"], ["strict", "Strict : tout bloquer"]].map(([k, l]) => (
                <button key={k} onClick={() => set({ entity_lockdown: k })}
                  className={`px-2.5 py-1 text-xs border ${(st.entity_lockdown || "off") === k ? "bg-red-600/20 border-red-500/40 text-white" : "bg-white/5 border-white/10 text-text-muted"}`}>{l}</button>
              ))}
            </div>
            {st.entity_lockdown === "strict" && <p className="text-[11px] text-amber-200 mt-2">Strict : même les véhicules créés côté joueur par vos scripts sont refusés. À réserver aux serveurs dont tous les scripts créent leurs entités côté serveur.</p>}
          </div>
        </div>
      </Panel>
      <Panel title="Anti-VPN et pays" icon={Globe}>
        <p className="text-xs text-text-muted mb-3">Utilisé par le module « VPN et pays » (Modules › Connexion). Il faut une clé gratuite proxycheck.io (1 000 vérifications par jour, chaque adresse est gardée 24 h). La clé reste sur le site, jamais sur votre serveur de jeu. Si le service ne répond pas, le joueur entre normalement.</p>
        <div className="grid lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-text-muted mb-1" htmlFor="proxycheck-key">Clé proxycheck.io</label>
            <input id="proxycheck-key" type="password" autoComplete="off" value={draft.secrets?.proxycheck_key || ""} onChange={(e) => setDraft({ ...draft, secrets: { ...(draft.secrets || {}), proxycheck_key: e.target.value.trim() } })}
              placeholder="Collez votre clé ici" className={`${inputCls} font-mono`} data-testid="proxycheck-key" />
            <p className="text-[11px] text-text-muted mt-1">Sans clé, le module ne vérifie rien.</p>
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1">Pays</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {[["off", "Tous les pays"], ["allow", "Seulement ces pays"], ["block", "Tous sauf ces pays"]].map(([k, label]) => (
                <button key={k} onClick={() => set({ countries_mode: k })}
                  className={`px-2.5 py-1 text-xs border ${st.countries_mode === k ? "bg-red-600/20 border-red-500/40 text-white" : "bg-white/5 border-white/10 text-text-muted"}`}>{label}</button>
              ))}
            </div>
            {st.countries_mode !== "off" && <ChipEditor values={draft.lists.countries || []} onChange={(v) => setDraft({ ...draft, lists: { ...draft.lists, countries: v.map((c) => c.toUpperCase()) } })} placeholder="FR, BE, CH…" testId="list-countries" />}
          </div>
        </div>
      </Panel>
      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Captures automatiques" icon={Camera}>
          <p className="text-xs text-text-muted mb-3">Prend régulièrement l'écran de quelques joueurs au hasard (liste blanche exclue). Les images arrivent dans Captures, sans message Discord.</p>
          <label className="flex items-center justify-between gap-3 text-sm mb-2">Fréquence
            <select value={st.auto_screens_minutes} onChange={(e) => set({ auto_screens_minutes: Number(e.target.value) })} className="bg-black/40 border border-white/10 px-2 py-1 text-xs">
              {[[0, "Désactivé"], [5, "Toutes les 5 min"], [10, "Toutes les 10 min"], [15, "Toutes les 15 min"], [30, "Toutes les 30 min"], [60, "Toutes les heures"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              {![0, 5, 10, 15, 30, 60].includes(Number(st.auto_screens_minutes)) && <option value={st.auto_screens_minutes}>Toutes les {st.auto_screens_minutes} min</option>}
            </select>
          </label>
          {st.auto_screens_minutes > 0 && <label className="flex items-center justify-between gap-3 text-sm">Joueurs par capture
            <input type="number" min={1} max={10} value={st.auto_screens_count} onChange={(e) => set({ auto_screens_count: Number(e.target.value) })} className="w-20 bg-black/40 border border-white/10 px-2 py-1 text-xs text-right" />
          </label>}
        </Panel>
        <Panel title="Rapport quotidien" icon={Activity}>
          <p className="text-xs text-text-muted mb-3">Un résumé des dernières 24 h (détections, expulsions, bans, détections les plus fréquentes) envoyé une fois par jour sur le webhook « Rapport quotidien ».</p>
          <label className="flex items-center justify-between gap-3 text-sm">Heure d'envoi (Paris)
            <select value={st.report_hour} onChange={(e) => set({ report_hour: Number(e.target.value) })} className="bg-black/40 border border-white/10 px-2 py-1 text-xs">
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{h} h</option>)}
            </select>
          </label>
          {!draft.webhooks.report && <p className="text-[11px] text-amber-200 mt-2">Ajoutez le webhook « Rapport quotidien » dans l'onglet Webhooks pour le recevoir.</p>}
        </Panel>
      </div>
      <Panel title="Écran en direct" icon={Monitor}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <label className="block">Images par seconde<input type="number" min={1} max={30} value={st.live_fps} onChange={(e) => set({ live_fps: Number(e.target.value) })} className={`${inputCls} mt-1`} /></label>
          <label className="block">Qualité (0,2 à 0,95)<input type="number" step={0.05} min={0.2} max={0.95} value={st.live_quality} onChange={(e) => set({ live_quality: Number(e.target.value) })} className={`${inputCls} mt-1`} /></label>
          <label className="block">Largeur de l'image
            <select value={st.live_width} onChange={(e) => set({ live_width: Number(e.target.value) })} className={`${inputCls} mt-1`}>{[640, 960, 1280, 1600, 1920].map((w) => <option key={w} value={w}>{w} px</option>)}</select>
          </label>
          <label className="block">Durée maximum (minutes)<input type="number" min={1} max={120} value={st.live_max_minutes} onChange={(e) => set({ live_max_minutes: Number(e.target.value) })} className={`${inputCls} mt-1`} /></label>
        </div>
        <label className="flex items-start gap-3 text-sm mt-4">
          <Toggle checked={!!st.notify_player_live} onChange={(v) => set({ notify_player_live: v })} label="Prévenir le joueur" />
          <span>Prévenir le joueur quand le staff regarde son écran<span className="block text-[11px] text-text-muted">Pensez à indiquer dans votre règlement que l'anti-cheat peut capturer l'image du jeu.</span></span>
        </label>
        <p className="text-[11px] text-text-muted mt-3">15 images/s en 960 px : environ 0,5 à 0,9 Mo/s par direct. La qualité baisse toute seule si la connexion du joueur ne suit pas. Moins d'images ou une largeur plus petite si la connexion du joueur est faible.</p>
      </Panel>
      <Panel title="Réinitialiser" icon={RefreshCw}>
        <p className="text-xs text-text-muted mb-3">Remet tous les modules, listes et paramètres par défaut (les webhooks sont gardés).</p>
        <Btn variant="danger" onClick={onReset}><RefreshCw className="w-4 h-4" />Réglages par défaut</Btn>
      </Panel>
      <p className="text-[11px] text-text-muted">Configuration v{draft.version} · licence {botId.slice(0, 8)}</p>
    </div>
  );
}

const ROLE_HELP = {
  viewer: "Voit tout (joueurs, détections, bans, captures), ne peut rien faire.",
  moderator: "Expulse, bannit, lève les bans, regarde l'écran en direct, lance les vagues.",
  admin: "Comme modérateur, plus les modules, listes, scripts, webhooks et paramètres.",
};

function TeamTab({ botId }) {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("moderator");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => ac.get(`/${botId}/team`).then(setData).catch((err) => toast.error(errorText(err, "Équipe indisponible"))), [botId]);
  useEffect(() => { load(); }, [load]);
  const add = async (mail, r) => {
    setBusy(true);
    try {
      await ac.post(`/${botId}/team`, { email: mail, role: r });
      toast.success("Accès donné");
      setEmail("");
      load();
    } catch (err) { toast.error(errorText(err, "Ajout impossible")); }
    setBusy(false);
  };
  const remove = async (m) => {
    if (!window.confirm(`Retirer l'accès de ${m.username || m.email} ?`)) return;
    try { await ac.del(`/${botId}/team/${m.user_id}`); toast.success("Accès retiré"); load(); } catch (err) { toast.error(errorText(err, "Erreur")); }
  };
  if (!data) return <p className="text-sm text-text-muted">Chargement…</p>;
  const roles = data.roles || {};
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Panel title={`Équipe (${data.members.length + 1})`} icon={UserCog} className="lg:col-span-2">
        <p className="text-xs text-text-muted mb-3">Donnez accès à ce tableau de bord à votre staff, sans partager votre compte. Chaque membre se connecte avec son propre compte VantaPulse et retrouve l'anti-cheat dans son tableau de bord.</p>
        <ul className="divide-y divide-border-default -mx-4" data-testid="team-list">
          <li className="px-4 py-2 flex flex-wrap items-center gap-3 text-sm">
            <span className="flex-1 min-w-[180px]"><span className="font-medium">{data.owner.username || "Propriétaire"}</span> <span className="text-xs text-text-muted">{data.owner.email}</span></span>
            <span className="text-xs px-2 py-0.5 border border-red-500/30 bg-red-500/10 text-red-200">Propriétaire</span>
          </li>
          {data.members.map((m) => (
            <li key={m.user_id} className="px-4 py-2 flex flex-wrap items-center gap-3 text-sm">
              <span className="flex-1 min-w-[180px]"><span className="font-medium">{m.username || m.email}</span> <span className="text-xs text-text-muted">{m.email}</span></span>
              {data.can_manage ? (
                <select value={m.role} onChange={(e) => add(m.email, e.target.value)} className="bg-black/40 border border-white/10 px-2 py-1 text-xs" aria-label={`Rôle de ${m.email}`}>
                  {Object.entries(roles).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              ) : <span className="text-xs text-text-secondary">{roles[m.role] || m.role}</span>}
              {data.can_manage && <button onClick={() => remove(m)} className="text-text-muted hover:text-red-300 p-1" aria-label={`Retirer ${m.email}`}><Trash2 className="w-3.5 h-3.5" /></button>}
            </li>
          ))}
        </ul>
        {data.can_manage ? (
          <form className="flex flex-wrap gap-2 mt-4" onSubmit={(e) => { e.preventDefault(); if (email.trim()) add(email.trim(), role); }}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail du compte VantaPulse" className="flex-1 min-w-[220px] bg-black/40 border border-white/10 px-2 py-1.5 text-sm" data-testid="team-email" />
            <select value={role} onChange={(e) => setRole(e.target.value)} className="bg-black/40 border border-white/10 px-2 py-1.5 text-sm" data-testid="team-role">
              {Object.entries(roles).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
            <Btn type="submit" variant="primary" disabled={busy || !email.trim()} data-testid="team-add"><Plus className="w-4 h-4" />Donner accès</Btn>
          </form>
        ) : <p className="text-xs text-text-muted mt-4">Seul le propriétaire de la licence peut modifier l'équipe.</p>}
      </Panel>
      <Panel title="Rôles" icon={Shield} className="self-start">
        <ul className="space-y-3 text-sm">
          {Object.entries(roles).map(([k, label]) => <li key={k}><div className="font-medium">{label}</div><div className="text-xs text-text-muted">{ROLE_HELP[k]}</div></li>)}
          <li><div className="font-medium">Propriétaire</div><div className="text-xs text-text-muted">Tout, plus l'équipe, la licence et le paiement.</div></li>
        </ul>
      </Panel>
    </div>
  );
}

function InstallTab({ botId, overview, owner }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { ac.get(`/${botId}/install`).then(setInfo).catch(() => {}); }, [botId]);
  const download = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/bots/${botId}/download`, { ...auth(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = info?.download_filename || "vantaguard.zip";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Téléchargement impossible"); }
    setBusy(false);
  };
  const [regenerating, setRegenerating] = useState(false);
  // Changer de serveur = nouvelle clé (l'ancienne ne marche plus nulle part).
  const regenerate = async () => {
    setRegenerating(true);
    try {
      await axios.post(`${API}/bots/${botId}/license/regenerate`, {}, auth());
      setInfo(await ac.get(`/${botId}/install`));
      toast.success("Nouvelle clé générée : remplace-la dans server.cfg");
    } catch (err) { toast.error(errorText(err, "Erreur")); }
    setRegenerating(false);
  };
  if (!info) return <p className="text-sm text-text-muted">Chargement…</p>;
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4 min-w-0">
        <Panel title="Installer VantaGuard" icon={Download}>
          <ol className="space-y-4 text-sm">
            <li><span className="font-medium">1. Téléchargez la ressource</span>
              {!owner ? <p className="mt-2 text-xs text-text-muted">Le téléchargement est réservé au propriétaire de la licence.</p> : <div className="mt-2"><Btn variant="primary" onClick={download} disabled={busy || !info.download_url}><Download className="w-4 h-4" />{busy ? "Préparation…" : `Télécharger${info.download_filename ? ` (${info.download_filename})` : ""}`}</Btn></div>}
            </li>
            <li><span className="font-medium">2. Mettez le dossier <code className="text-emerald-200">vantaguard</code> dans <code className="text-emerald-200">resources/</code></span></li>
            <li><span className="font-medium">3. Ajoutez ces lignes dans server.cfg, avant les autres ressources</span>
              <div className="mt-2"><CodeLines lines={info.server_cfg} secret={info.license_key} /></div>
              <p className="text-[11px] text-text-muted mt-1">La clé reste dans server.cfg (« set » et non « sets » : elle n'est pas publique). Ne la partagez pas.</p>
            </li>
            <li><span className="font-medium">4. Redémarrez le serveur</span> : la console affiche « Protection active » et ce tableau de bord passe « En ligne ».</li>
            <li><span className="font-medium">5. Conseillé : activez le mode test 1 à 2 jours</span> (Paramètres), vérifiez les détections, puis désactivez-le.</li>
            <li><span className="font-medium">Optionnel :</span> la ressource officielle <code className="text-emerald-200">screenshot-basic</code> sert de secours pour les captures si la vue du jeu n'est pas disponible.</li>
          </ol>
        </Panel>
        <Panel title="Pour vos scripts (exports)" icon={Code2}>
          <CodeLines lines={[
            "-- Autoriser une téléportation (garage, appartement, spawn…) pendant 5 s",
            "exports.vantaguard:AllowTeleport(source, 5)",
            "-- Exempter un joueur d'un module (nitro, menu admin…) pendant 60 s",
            "exports.vantaguard:SetBypass(source, 'vehicle_modifiers', 60)",
            "-- Bannir depuis un script (renvoie le code du ban)",
            "local code = exports.vantaguard:BanPlayer(source, 'Duplication d\\'argent', 0)",
            "-- Signaler une triche repérée par votre script ('log', 'kick' ou 'ban')",
            "exports.vantaguard:Flag(source, 'Prix de vente impossible', 'log', { prix = 999999 })",
          ]} />
        </Panel>
        <Panel title="Commandes du staff en jeu (permission vantaguard.staff)" icon={BookOpen}>
          <ul className="text-sm space-y-1.5">
            <li><code className="text-emerald-200">/vgstatus</code> : état de la protection</li>
            <li><code className="text-emerald-200">/vgban &lt;id&gt; &lt;heures, 0 = définitif&gt; &lt;raison&gt;</code></li>
            <li><code className="text-emerald-200">/vgunban &lt;code&gt;</code></li>
            <li><code className="text-emerald-200">/vgscreen &lt;id&gt;</code> : capture envoyée ici et sur Discord</li>
            <li><code className="text-emerald-200">/vgwave</code> : lance tout de suite la vague de bans en attente</li>
            <li><code className="text-emerald-200">/vgreload</code> : recharge la configuration et les bans</li>
          </ul>
        </Panel>
      </div>
      <Panel title="Licence" icon={ShieldCheck} className="self-start">
        <div className="space-y-2 text-sm">
          <div><span className="text-text-muted">Clé</span>
            <div className="mt-1.5"><SecretKey value={info.license_key || ""} compact testid="ac-license" regenerating={regenerating} onRegenerate={owner ? regenerate : undefined} /></div>
          </div>
          <div className="flex justify-between"><span className="text-text-muted">Serveur lié</span><span>{info.bound ? (info.server_name || "oui") : "aucun"}</span></div>
          <div className="flex justify-between"><span className="text-text-muted">Version installée</span><span>{overview?.server?.version || "—"}</span></div>
          <div className="flex justify-between"><span className="text-text-muted">Dernière version</span><span>{overview?.latest_version || "—"}</span></div>
        </div>
      </Panel>
    </div>
  );
}

// ── Page ──

export default function AntiCheatPage() {
  const { botId } = useParams();
  const [params, setParams] = useSearchParams();
  const [overview, setOverview] = useState(null);
  const role = overview?.access?.role || "viewer";
  const rank = RANK[role] || 1;
  const tabs = TABS.filter((t) => !ADMIN_TABS.includes(t.id) || rank >= RANK.admin);
  const wanted = params.get("tab");
  const tab = tabs.some((t) => t.id === wanted) ? wanted : (!overview && TABS.some((t) => t.id === wanted) ? wanted : "overview");
  const setTab = (id) => setParams({ tab: id }, { replace: true });
  const [catalog, setCatalog] = useState(null);
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const loadOverview = useCallback(() => ac.get(`/${botId}/overview`).then(setOverview).catch((err) => { if (err?.response?.status === 404) setNotFound(true); }), [botId]);
  useEffect(() => { loadOverview(); const t = setInterval(loadOverview, 6000); return () => clearInterval(t); }, [loadOverview]);
  useEffect(() => { ac.get("/catalog").then(setCatalog).catch(() => {}); }, []);
  const isAdmin = rank >= RANK.admin;
  useEffect(() => {
    if (!isAdmin) return;
    ac.get(`/${botId}/config`).then((cfg) => { setSaved(cfg); setDraft(cfg); }).catch(() => {});
  }, [botId, isAdmin]);

  const dirty = useMemo(() => saved && draft && JSON.stringify(saved) !== JSON.stringify(draft), [saved, draft]);
  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const save = async () => {
    setSaving(true);
    try {
      const cfg = await ac.put(`/${botId}/config`, draft);
      setSaved(cfg); setDraft(cfg);
      toast.success(overview?.server?.online ? "Enregistré : appliqué sur le serveur dans quelques secondes" : "Enregistré : appliqué au prochain démarrage du serveur");
      loadOverview();
    } catch (err) { toast.error(errorText(err, "Enregistrement impossible")); }
    setSaving(false);
  };

  const reset = async () => {
    if (!window.confirm("Remettre tous les réglages par défaut ?")) return;
    try { const cfg = await ac.post(`/${botId}/config/reset`); setSaved(cfg); setDraft(cfg); toast.success("Réglages par défaut remis"); } catch (err) { toast.error(errorText(err, "Erreur")); }
  };

  const whitelistPlayer = async (player) => {
    const license = (player.ids || []).find((i) => i.startsWith("license:")) || (player.ids || [])[0];
    if (!license || !saved) return;
    if (saved.whitelist.some((w) => w.identifier === license)) { toast.info("Déjà dans la liste blanche"); return; }
    try {
      const cfg = await ac.put(`/${botId}/config`, { ...saved, whitelist: [...saved.whitelist, { identifier: license, note: player.name }] });
      setSaved(cfg);
      setDraft(dirty ? { ...draft, whitelist: cfg.whitelist, version: cfg.version } : cfg);
      toast.success(`${player.name} ajouté à la liste blanche`);
    } catch (err) { toast.error(errorText(err, "Erreur")); }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white"><Navbar />
        <main className="max-w-3xl mx-auto px-4 pt-28"><Panel><p className="text-sm">Licence VantaGuard introuvable sur votre compte. <Link to="/dashboard" className="underline">Retour au tableau de bord</Link></p></Panel></main>
      </div>
    );
  }

  const server = overview?.server;
  const configTab = CONFIG_TABS.includes(tab);
  const sections = tabs.map((t) => ({ ...t, hint: TAB_HINTS[t.id] }));
  const brand = (
    <div className="flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/5 px-3 py-2.5">
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500 to-rose-700 flex items-center justify-center shrink-0"><Shield className="w-4 h-4 text-white" /></div>
      <div className="min-w-0">
        <p className="text-sm font-semibold leading-tight">VantaGuard</p>
        <p className="text-[11px] text-white/40 truncate">{overview?.access?.label || "Anti-cheat FiveM"}</p>
      </div>
    </div>
  );
  return (
    <PanelShell brand={brand} sections={sections} active={tab} onSelect={setTab} back={{ to: "/dashboard", label: "Tableau de bord" }} testid="ac"
      header={<>

        <div className="flex flex-wrap items-center gap-4 mb-5">
          <div className="w-12 h-12 bg-red-500/10 border border-red-500/30 flex items-center justify-center"><Shield className="w-6 h-6 text-red-400" /></div>
          <div className="flex-1 min-w-[200px]">
            <h1 className="font-heading text-2xl font-bold">VantaGuard</h1>
            <p className="text-sm text-text-muted">{server?.server_name || overview?.license?.server_name || "Anti-cheat FiveM"}{server?.version ? ` · v${server.version}` : ""}</p>
          </div>
          {overview?.access && overview.access.role !== "owner" && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-sky-500/30 bg-sky-500/10 text-sky-200" data-testid="access-role"><UserCog className="w-4 h-4" />{overview.access.label}</span>
          )}
          <span data-testid="server-status" className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm border ${server?.online ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" : "border-white/10 bg-white/5 text-text-muted"}`}>
            <span className={`w-2 h-2 rounded-full ${server?.online ? "bg-emerald-400" : "bg-white/30"}`} />
            {server?.online ? `En ligne · ${server.players_count} joueur(s)` : server?.known ? `Hors ligne depuis ${since(server.last_seen)}` : "Jamais connecté"}
          </span>
        </div>
        {overview && !overview.license.ok && (
          <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 px-3 py-2 text-sm text-red-100 mb-4"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{overview.license.problem} : la protection est arrêtée sur le serveur. <Link to={`/bot/vantaguard`} className="underline ml-1">Renouveler</Link></div>
        )}
        {overview?.test_mode && (
          <div className="flex items-start gap-2 bg-violet-500/10 border border-violet-500/30 px-3 py-2 text-sm text-violet-100 mb-4"><FlaskConical className="w-4 h-4 mt-0.5 shrink-0" />Mode test actif : rien n'est sanctionné. {isAdmin && <button className="underline ml-1" onClick={() => setTab("settings")}>Paramètres</button>}</div>
        )}
        {overview && !overview.server.known && tab === "overview" && (
          <div className="flex items-start gap-2 bg-sky-500/10 border border-sky-500/30 px-3 py-2 text-sm text-sky-100 mb-4"><BookOpen className="w-4 h-4 mt-0.5 shrink-0" />Votre serveur ne s'est pas encore connecté. {isAdmin && <button className="underline ml-1" onClick={() => setTab("install")}>Voir l'installation</button>}</div>
        )}
      </>}>


        {tab === "overview" && <OverviewTab overview={overview} onTab={setTab} />}
        {tab === "players" && <PlayersTab botId={botId} onWhitelist={whitelistPlayer} rank={rank} />}
        {tab === "detections" && <DetectionsTab botId={botId} catalog={catalog} rank={rank} />}
        {tab === "bans" && <BansTab botId={botId} rank={rank} wave={overview?.wave} onChange={loadOverview} />}
        {tab === "team" && overview && <TeamTab botId={botId} />}
        {tab === "captures" && <CapturesTab botId={botId} />}
        {tab === "logs" && <LogsTab botId={botId} />}
        {tab === "install" && overview && <InstallTab botId={botId} overview={overview} owner={role === "owner"} />}
        {configTab && (!draft || !catalog ? <p className="text-sm text-text-muted">Chargement…</p> : (
          <>
            {tab === "modules" && <ModulesTab draft={draft} setDraft={setDraft} catalog={catalog} />}
            {tab === "lists" && <ListsTab draft={draft} setDraft={setDraft} catalog={catalog} />}
            {tab === "scripts" && <ScriptsTab botId={botId} draft={draft} setDraft={setDraft} />}
            {tab === "webhooks" && <WebhooksTab botId={botId} draft={draft} setDraft={setDraft} catalog={catalog} />}
            {tab === "settings" && <SettingsTab botId={botId} draft={draft} setDraft={setDraft} catalog={catalog} onReset={reset} />}
          </>
        ))}
      {dirty && (
        <div className="fixed bottom-0 left-0 lg:left-64 right-0 z-40 bg-[#0c0c14]/95 backdrop-blur border-t border-blue-500/30" data-testid="save-bar">
          <div className="max-w-6xl px-4 lg:px-8 py-3 flex flex-wrap items-center gap-3">
            <span className="text-sm flex-1 min-w-[200px]">Modifications non enregistrées</span>
            <Btn onClick={() => setDraft(saved)}>Annuler</Btn>
            <Btn variant="primary" onClick={save} disabled={saving} data-testid="save-config">{saving ? "Enregistrement…" : "Enregistrer et appliquer"}</Btn>
          </div>
        </div>
      )}
    </PanelShell>
  );
}
